import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import "react-day-picker/style.css";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from "chart.js";

import styles from "./overview.module.css";

import Button from "../../../components/button/button";
import { downloadModelFiles } from "../../../utils/modelFiles";
import ActionQueue from "../../../components/actionItems/actionQueue";
import IndicatorTraffic from "../../../components/indicators/indicatorTraffic";
import DelinquencyPredictedModal from "../../../components/predictive/delinquencyPredictedModal/delinquencyPredictedModal";
import DropoutRiskModal from "../../../components/predictive/dropoutRiskModal/dropoutRiskModal";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";

import { useInstitution } from "../../../context/InstitutionContext";
import { financeInstitution } from "../../../services/finance";
import { listFiles } from "../../../services/import";
import { getFavoriteDashboard } from "../../../services/preferences";
import {
  delinquencyTrend,
  dropoutRiskDashboard,
  dropoutRiskResults
} from "../../../services/prediction";

import { addPorcentage, formatDate, formatNumberToCurrency } from "../../../utils/formatter";
import useOutsideAlerter from "../../../utils/clickOutside";
import { getDateRange, saveDateRange } from "../../../utils/localStorage";

import loadingGif from "../../../assets/loading.gif";
import CalendarIcon from "../../../assets/icons/calendar.svg";
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg";
import DashboardIcon from "../../../assets/icons/dashboard-icon.svg";
import TargetIcon from "../../../assets/icons/target.svg";
import ChartCourseUpIcon from "../../../assets/icons/chart-course-up.svg";
import ChartCourseDownIcon from "../../../assets/icons/chart-course-down.svg";
import TriangleExclamationIcon from "../../../assets/icons/triangle-exclamation-blue.svg";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// ponytail: uma página só de resultados de evasão cobre o card e a lista de ação
// prioritária. Acima de 1000 classificados a contagem de "alto risco" subestima —
// aí vale pedir um agregado ao backend.
const DROPOUT_PAGE_SIZE = 1000;

const nextMonthLabel = () => {
  const now = new Date();
  const idx = (now.getMonth() + 1) % 12;
  const year = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  return `${MONTHS[idx]}/${String(year).slice(-2)}`;
};

const share = (part, total) => {
  if (!total) {
    return 0;
  }
  return (part / total) * 100;
};

const Overview = () => {
  const navigate = useNavigate();
  const { institution } = useInstitution();
  const calendarRef = useRef(null);
  const actionsRef = useRef(null);

  const scrollToActions = useCallback(() => {
    actionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const [checkingFavorite, setCheckingFavorite] = useState(true);
  const [hasFiles, setHasFiles] = useState(undefined);
  // começa carregando: nenhum indicador aparece antes da primeira resposta
  const [loading, setLoading] = useState(true);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [finance, setFinance] = useState(null);
  const [trend, setTrend] = useState(null);
  const [dropoutDashboard, setDropoutDashboard] = useState(null);
  const [dropoutStudents, setDropoutStudents] = useState([]);
  const [dropoutTotal, setDropoutTotal] = useState(0);
  // drill-down dos números do card de inadimplência
  const [showPredictedStudents, setShowPredictedStudents] = useState(false);
  const [dropoutBandRates, setDropoutBandRates] = useState(null);
  const [dropoutProbabilityStatus, setDropoutProbabilityStatus] = useState(null);
  // null fechado; "all" | "alto" abre o modal já filtrado pelo número clicado
  const [dropoutModalMode, setDropoutModalMode] = useState(null);

  const [dateRange, setDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  // Preferência já existente: quem fixou um dashboard customizado continua caindo nele.
  useEffect(() => {
    const checkFavoriteDashboard = async () => {
      try {
        const favoriteDashboardId = await getFavoriteDashboard();
        if (favoriteDashboardId) {
          navigate(`/app/dashboard-customizado/${favoriteDashboardId}`, { replace: true });
          return;
        }
      } catch (error) {
        console.error("Erro ao verificar dashboard favorito:", error);
      } finally {
        setCheckingFavorite(false);
      }
    };

    checkFavoriteDashboard();
  }, [navigate]);

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  useEffect(() => {
    if (!institution?.id) {
      return;
    }

    const fetchFiles = async () => {
      try {
        const files = await listFiles(institution.id);
        setHasFiles(!!(files && files.length));
      } catch (error) {
        console.error("Erro ao buscar arquivos:", error);
        setHasFiles(false);
      }
    };

    fetchFiles();
  }, [institution?.id]);

  const fetchAll = useCallback(async () => {
    const institutionId = institution?.id;
    if (!institutionId) {
      return;
    }

    const startDate = formatDate(dateRange?.from).replaceAll("/", "-");
    const endDate = formatDate(dateRange?.to).replaceAll("/", "-");

    setLoading(true);

    // allSettled: um indicador indisponível não pode derrubar a tela inteira.
    const [financeRes, trendRes, dashboardRes, resultsRes] = await Promise.allSettled([
      financeInstitution({ institutionId, startDate, endDate, filtershift: undefined }),
      delinquencyTrend({ institutionId }),
      dropoutRiskDashboard({ institutionId }),
      dropoutRiskResults({ institutionId, pageNumber: 1, pageSize: DROPOUT_PAGE_SIZE })
    ]);

    if (financeRes.status === "fulfilled") {
      setFinance(financeRes.value?.financeData || financeRes.value || null);
    }

    if (trendRes.status === "fulfilled") {
      const value = trendRes.value;
      setTrend(value?.delinquencyTrend || value?.DelinquencyTrend || value || null);
    }

    if (dashboardRes.status === "fulfilled") {
      setDropoutDashboard(dashboardRes.value?.dropoutRiskDashboard || null);
    }

    if (resultsRes.status === "fulfilled") {
      const result = resultsRes.value?.dropoutRiskResults;
      setDropoutStudents(result?.data || []);
      setDropoutTotal(result?.pagination?.totalCount || result?.data?.length || 0);
      setDropoutBandRates(result?.bandRates || null);
      setDropoutProbabilityStatus(result?.probabilityStatus || null);
    }

    setLoading(false);
    setIsCalendarOpen(false);
  }, [institution?.id, dateRange]);

  useEffect(() => {
    if (hasFiles === true) {
      fetchAll();
    }
  }, [hasFiles, fetchAll]);

  const dateRangeString = `${formatDate(dateRange?.from)} – ${formatDate(dateRange?.to)}`;

  const delinquency = useMemo(() => {
    const rows = trend?.monthlyData || trend?.MonthlyData || [];
    const labels = rows.map((m) => m.month || m.Month);
    const counts = rows.map((m) => m.delinquentStudents ?? m.DelinquentStudents ?? 0);

    const prediction = trend?.prediction || trend?.Prediction;
    const predicted = prediction?.predictedDelinquents ?? prediction?.PredictedDelinquents ?? null;

    const last = counts.length > 0 ? counts[counts.length - 1] : 0;
    const previous = counts.length > 1 ? counts[counts.length - 2] : last;

    let direction = "estabilidade";
    if (predicted != null && predicted > last) {
      direction = "leve alta";
    } else if (predicted != null && predicted < last) {
      direction = "queda";
    }

    const previousMonthLabel = labels.length > 1 ? labels[labels.length - 1] : null;
    let previousMove = "estabilidade";
    if (last > previous) {
      previousMove = "alta";
    } else if (last < previous) {
      previousMove = "queda";
    }

    // ponytail: valor em risco = alunos previstos × ticket médio. Vira número exato
    // no dia em que o preditivo devolver o montante em aberto por aluno pontuado.
    const averageTicket = finance?.averageTicket ?? 0;
    const amountAtRisk = predicted != null ? predicted * averageTicket : null;

    return {
      labels,
      counts,
      predicted,
      last,
      direction,
      previousMonthLabel,
      previousMove,
      amountAtRisk
    };
  }, [trend, finance]);

  const dropout = useMemo(() => {
    const breakdown = dropoutDashboard?.criteriaBreakdown || [];
    const byCourse = dropoutDashboard?.dropoutByCourse || [];

    const criteriaTotal = breakdown.reduce((sum, item) => sum + (item.count || 0), 0);
    const criteria = breakdown
      .map((item) => ({
        label: item.criteria,
        count: item.count || 0,
        pct: share(item.count || 0, criteriaTotal)
      }))
      .sort((a, b) => b.pct - a.pct);

    const courseTotal = byCourse.reduce((sum, item) => sum + (item.studentCount || 0), 0);
    const courses = [...byCourse]
      .sort((a, b) => (b.studentCount || 0) - (a.studentCount || 0))
      .map((item) => ({
        label: item.courseName,
        count: item.studentCount || 0,
        pct: share(item.studentCount || 0, courseTotal)
      }));
    const topCourses = courses.slice(0, 2);
    const topCoursesShare = topCourses.reduce((sum, item) => sum + item.pct, 0);

    const highRisk = dropoutStudents.filter((student) => student.riskBand === "alto");
    const minHighScore = highRisk.reduce(
      (min, student) => Math.min(min, student.matchedCriteriaCount ?? min),
      Infinity
    );

    return {
      criteria,
      courses,
      topCourses,
      topCoursesShare,
      highRiskCount: highRisk.length,
      minHighScore: Number.isFinite(minHighScore) ? minHighScore : null
    };
  }, [dropoutDashboard, dropoutStudents]);

  // Insights derivados dos indicadores já carregados — sem chamada extra.
  const insights = useMemo(() => {
    const items = [];

    const rates = finance?.monthlyDelinquencyRates || [];
    if (rates.length > 1) {
      const first = rates[0];
      const last = rates[rates.length - 1];
      const moved = last.delinquencyRate < first.delinquencyRate ? "caiu" : "subiu";
      items.push({
        icon: last.delinquencyRate < first.delinquencyRate ? ChartCourseDownIcon : ChartCourseUpIcon,
        text: `A taxa de inadimplência ${moved} de ${addPorcentage(first.delinquencyRate)} (${first.month}) para ${addPorcentage(last.delinquencyRate)} (${last.month}).`
      });
    }

    const topCriteria = dropout.criteria[0];
    if (topCriteria) {
      items.push({
        icon: TriangleExclamationIcon,
        text: `${topCriteria.label} aparece em ${addPorcentage(topCriteria.pct)} dos alunos em risco de evasão.`
      });
    }

    const averageTicket = finance?.averageTicket ?? 0;
    if (dropout.highRiskCount > 0 && averageTicket > 0) {
      items.push({
        icon: TargetIcon,
        text: `Contato preventivo com os ${dropout.highRiskCount} alunos de alto risco pode preservar até ${formatNumberToCurrency(dropout.highRiskCount * averageTicket)} em mensalidades.`
      });
    }

    return items;
  }, [finance, dropout]);

  const chartData = useMemo(() => {
    const { counts, labels, predicted, last } = delinquency;
    const visibleLabels = labels.slice(-8);
    const visibleCounts = counts.slice(-8);
    const hasPrediction = predicted != null && predicted > 0;

    return {
      labels: [...visibleLabels, ...(hasPrediction ? [nextMonthLabel()] : [])],
      datasets: [
        {
          label: "Realizado",
          data: [...visibleCounts, ...(hasPrediction ? [null] : [])],
          borderColor: "#0962AD",
          backgroundColor: "rgba(9,98,173,0.08)",
          borderWidth: 2.5,
          tension: 0.3,
          pointRadius: 4,
          pointBackgroundColor: "#0962AD",
          pointBorderColor: "#fff",
          fill: true
        },
        ...(hasPrediction
          ? [
              {
                label: "Previsão",
                data: [...Array(Math.max(0, visibleCounts.length - 1)).fill(null), last, predicted],
                borderColor: "#469BE3",
                borderWidth: 2.5,
                borderDash: [6, 4],
                tension: 0.3,
                pointRadius: 5,
                pointBackgroundColor: "#469BE3",
                pointBorderColor: "#fff",
                pointStyle: "rectRot",
                fill: false
              }
            ]
          : [])
      ]
    };
  }, [delinquency]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: { usePointStyle: true, padding: 16, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => (ctx.parsed.y == null ? null : `${ctx.dataset.label}: ${ctx.parsed.y}`)
        }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#64748b", font: { size: 12 } } },
      y: {
        beginAtZero: true,
        ticks: { color: "#64748b", precision: 0, callback: (v) => (Number.isInteger(v) ? v : null) },
        grid: { color: "#f1f5f9" }
      }
    }
  };

  const sparkOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
    elements: { point: { radius: 0 } }
  };

  if (checkingFavorite || hasFiles === undefined) {
    return (
      <div className={styles.wrapper}>
        <HeaderTitle title="Visão Geral" icon={DashboardIcon} />
        <div className={styles.loadingWrapper}>
          <img src={loadingGif} alt="Carregando..." />
        </div>
      </div>
    );
  }

  if (hasFiles === false) {
    return (
      <div className={styles.wrapper}>
        <HeaderTitle
          title="Visão Geral"
          subtitle="Importe seus dados para começar a acompanhar os indicadores da instituição"
          icon={DashboardIcon}
        >
          <div className={styles.headerActions}>
            <Button icon={CalendarIcon} text={dateRangeString} size="normal" variation="outline" disabled={true} />
            <Button icon={BarsFilterIcon} text="Filtrar" size="normal" variation="outline" disabled={true} />
          </div>
        </HeaderTitle>

        <div className={styles.body}>
          <section className={styles.emptyHero}>
            <svg className={styles.emptyIcon} viewBox="0 0 64 40" aria-hidden="true">
              <line x1="4" y1="34" x2="60" y2="34" />
              <line x1="18" y1="14" x2="18" y2="30" />
              <line x1="32" y1="6" x2="32" y2="30" />
              <line x1="46" y1="18" x2="46" y2="30" />
            </svg>

            <h2 className={styles.emptyTitle}>Nenhum dado importado ainda</h2>
            <p className={styles.emptyText}>
              Importe suas planilhas financeiras e acadêmicas para gerar os indicadores e ativar a
              análise preditiva de inadimplência e evasão.
            </p>

            <div className={styles.emptyActions}>
              <Button
                text="Importar dados"
                size="normal"
                variation="primary"
                onClick={() => navigate("/app/importar-dados")}
              />
              <Button
                text="Baixar modelo de planilha"
                size="normal"
                variation="outline"
                onClick={downloadModelFiles}
              />
            </div>

            <span className={styles.emptyHint}>
              Formatos aceitos: .xlsx, .csv · O mapeamento das colunas é automático
            </span>
          </section>

          <div className={styles.kpiRow}>
            {["Receitas", "Ticket Médio Mensal", "Alunos Matriculados", "Taxa de Inadimplência"].map((title) => (
              <div key={title} className={styles.skeletonCard}>
                <span className={styles.skeletonTitle}>{title}</span>
                <span className={styles.skeletonBar} />
                <span className={`${styles.skeletonBar} ${styles.skeletonBarShort}`} />
              </div>
            ))}
          </div>

          <div className={styles.insightsRow}>
            <section className={styles.panel}>
              <h3 className={styles.panelTitle}>Como começar</h3>
              <ol className={styles.stepList}>
                <li className={styles.stepItem}>
                  <span className={`${styles.stepNumber} ${styles.stepNumberDone}`}>1</span>
                  <div>
                    <strong className={styles.stepTitle}>Crie sua conta e instituição</strong>
                    <span className={styles.stepText}>Concluído</span>
                  </div>
                </li>
                <li className={styles.stepItem}>
                  <span className={styles.stepNumber}>2</span>
                  <div>
                    <strong className={styles.stepTitle}>Importe as planilhas</strong>
                    <span className={styles.stepText}>
                      Dados de alunos, mensalidades e frequência — em qualquer formato de colunas
                    </span>
                  </div>
                </li>
                <li className={styles.stepItem}>
                  <span className={styles.stepNumber}>3</span>
                  <div>
                    <strong className={styles.stepTitle}>Acompanhe os indicadores</strong>
                    <span className={styles.stepText}>
                      Dashboard, predição de inadimplência e evasão são gerados automaticamente
                    </span>
                  </div>
                </li>
              </ol>
            </section>

            <section className={styles.panel}>
              <h3 className={styles.panelTitle}>O que você vai ver aqui</h3>
              <p className={styles.previewText}>
                Indicadores financeiros e acadêmicos do período, evolução de inadimplentes com
                projeção e a lista de alunos que precisam de ação prioritária.
              </p>
              <svg className={styles.previewChart} viewBox="0 0 220 60" aria-hidden="true">
                <line x1="0" y1="56" x2="220" y2="56" />
                <polyline points="4,48 40,40 78,20 112,14 148,26 180,44 196,48" />
                <line x1="196" y1="48" x2="216" y2="44" strokeDasharray="5 4" />
              </svg>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <HeaderTitle
        title="Visão Geral"
        subtitle="Indicadores financeiros, acadêmicos e análise preditiva da instituição"
        icon={DashboardIcon}
      >
        <div className={styles.headerActions}>
          <div className={styles.calendarAnchor}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calendarPopover}>
                <DayPicker
                  mode="range"
                  selected={dateRange}
                  defaultMonth={dateRange?.from}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setDateRange(range);
                    }
                  }}
                  locale={ptBR}
                  captionLayout="dropdown"
                />
              </div>
            )}
          </div>

          {/* ponytail: sem filtros extras definidos ainda — mesmo estado do dashboard atual. */}
          <Button icon={BarsFilterIcon} text="Filtrar" size="normal" variation="outline" disabled={true} />
        </div>
      </HeaderTitle>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.loadingWrapper}>
            <img src={loadingGif} alt="Carregando..." />
          </div>
        ) : (
        <>
        <div className={styles.predictionRow}>
          <section className={styles.predictionCard}>
            <div className={styles.predictionHeader}>
              <span className={styles.eyebrow}>PREDIÇÃO · INADIMPLÊNCIA</span>
              <span className={styles.cardLink} onClick={() => navigate("/app/predicao")}>
                Ver análise →
              </span>
            </div>
            <h3 className={styles.predictionTitle}>Previsão para os próximos 30 dias</h3>

            <div className={styles.predictionBody}>
              {/* Os dois números abrem a lista nominal por trás deles. */}
              <button
                type="button"
                className={`${styles.metric} ${styles.metricButton}`}
                onClick={() => setShowPredictedStudents(true)}
                disabled={delinquency.predicted == null}
                title="Ver quais alunos compõem este número"
              >
                <strong className={styles.metricValue}>{delinquency.predicted ?? "—"}</strong>
                <span className={styles.metricLabel}>alunos com risco previsto</span>
              </button>
              <button
                type="button"
                className={`${styles.metric} ${styles.metricButton}`}
                onClick={() => setShowPredictedStudents(true)}
                disabled={delinquency.amountAtRisk == null}
                title="Ver como este valor é calculado"
              >
                <strong className={`${styles.metricValue} ${styles.metricValueDanger}`}>
                  {delinquency.amountAtRisk != null ? formatNumberToCurrency(delinquency.amountAtRisk) : "—"}
                </strong>
                <span className={styles.metricLabel}>valor em risco estimado</span>
              </button>
              <div className={styles.sparkline}>
                <Line data={chartData} options={sparkOptions} />
              </div>
            </div>

            <p className={styles.note}>
              {delinquency.predicted != null
                ? `Tendência de ${delinquency.direction}${
                    delinquency.previousMonthLabel
                      ? ` após ${delinquency.previousMove} em ${delinquency.previousMonthLabel}`
                      : ""
                  }. `
                : "Sem previsão disponível para o período. "}
              Critérios: histórico de atraso e parcelas vencidas.
            </p>
          </section>

          <section className={styles.predictionCard}>
            <div className={styles.predictionHeader}>
              <span className={`${styles.eyebrow} ${styles.eyebrowDanger}`}>PREDIÇÃO · EVASÃO</span>
              <span className={styles.cardLink} onClick={() => navigate("/app/possiveis-evasores")}>
                Ver análise →
              </span>
            </div>
            <h3 className={styles.predictionTitle}>Alunos em risco de evasão</h3>

            <div className={styles.predictionBody}>
              {/* Os dois números abrem a lista nominal, já filtrada pelo que foi clicado. */}
              <button
                type="button"
                className={`${styles.metric} ${styles.metricButton}`}
                onClick={() => setDropoutModalMode("all")}
                disabled={!dropoutStudents.length}
                title="Ver quais alunos compõem este número"
              >
                <strong className={styles.metricValue}>{dropoutTotal || "—"}</strong>
                <span className={styles.metricLabel}>alunos classificados</span>
              </button>
              <button
                type="button"
                className={`${styles.metric} ${styles.metricButton}`}
                onClick={() => setDropoutModalMode("alto")}
                disabled={!dropout.highRiskCount}
                title="Ver quais alunos estão em alto risco"
              >
                <strong className={`${styles.metricValue} ${styles.metricValueDanger}`}>
                  {dropout.highRiskCount || "—"}
                </strong>
                <span className={styles.metricLabel}>
                  alto risco{dropout.minHighScore != null ? ` · score ≥ ${dropout.minHighScore}` : ""}
                </span>
              </button>

              <ul className={styles.criteriaList}>
                {dropout.criteria.slice(0, 3).map((item) => (
                  <li key={item.label} className={styles.criteriaRow}>
                    <span className={styles.criteriaLabel}>{item.label}</span>
                    <span className={styles.criteriaTrack}>
                      <span className={styles.criteriaFill} style={{ width: `${item.pct.toFixed(1)}%` }} />
                    </span>
                    <span className={styles.criteriaValue}>{addPorcentage(item.pct)}</span>
                  </li>
                ))}
                {dropout.criteria.length === 0 && (
                  <li className={styles.criteriaEmpty}>Sem critérios classificados no período.</li>
                )}
              </ul>
            </div>

            <p className={styles.note}>
              {dropout.topCourses.length > 0
                ? `${dropout.topCourses.map((c) => c.courseName).join(" e ")} concentram ${addPorcentage(
                    dropout.topCoursesShare
                  )} dos alunos em risco.`
                : "Ainda não há distribuição por curso para os alunos em risco."}
            </p>
          </section>
        </div>

        {showPredictedStudents && (
          <DelinquencyPredictedModal
            institutionId={institution?.id}
            averageTicket={finance?.averageTicket ?? 0}
            onClose={() => setShowPredictedStudents(false)}
          />
        )}

        {dropoutModalMode && (
          <DropoutRiskModal
            students={dropoutStudents}
            total={dropoutTotal}
            bandRates={dropoutBandRates}
            probabilityStatus={dropoutProbabilityStatus}
            minHighScore={dropout.minHighScore}
            initialMode={dropoutModalMode}
            onClose={() => setDropoutModalMode(null)}
          />
        )}


        <IndicatorTraffic institutionId={institution?.id} onActionClick={scrollToActions} />

        <div ref={actionsRef}>
          <ActionQueue institutionId={institution?.id} />
        </div>

        <div className={styles.bottomRow}>
          <section className={styles.panel}>
            <h3 className={styles.panelTitle}>Evolução de alunos inadimplentes</h3>
            <span className={styles.panelSubtitle}>Últimos 8 meses + projeção dos próximos 30 dias</span>
            <div className={styles.chartWrapper}>
              {delinquency.counts.length > 0 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <p className={styles.emptyPanel}>Sem série histórica de inadimplência disponível.</p>
              )}
            </div>
          </section>

          <section className={styles.panel}>
            <h3 className={styles.panelTitle}>Risco de evasão por curso</h3>
            <span className={styles.panelSubtitle}>Distribuição dos alunos classificados</span>

            <ul className={styles.barList}>
              {dropout.courses.slice(0, 5).map((course) => (
                <li key={course.label} className={styles.barRow}>
                  <span className={styles.barLabel}>{course.label}</span>
                  <span className={styles.barTrack}>
                    <span className={styles.barFill} style={{ width: `${course.pct.toFixed(1)}%` }} />
                  </span>
                  <span className={styles.barValue}>{addPorcentage(course.pct)}</span>
                </li>
              ))}
              {dropout.courses.length === 0 && (
                <li className={styles.emptyPanel}>Sem distribuição por curso disponível.</li>
              )}
            </ul>
          </section>
        </div>

        <div className={styles.insightsRow}>
          <section className={styles.panel}>
            <h3 className={styles.panelTitle}>Insights do período</h3>
            <span className={styles.panelSubtitle}>Gerados automaticamente a partir dos indicadores</span>

            <ul className={styles.insightList}>
              {insights.map((insight) => (
                <li key={insight.text} className={styles.insightItem}>
                  <img src={insight.icon} alt="" className={styles.insightIcon} />
                  <span>{insight.text}</span>
                </li>
              ))}
              {insights.length === 0 && (
                <li className={styles.emptyPanel}>Sem insights para o período selecionado.</li>
              )}
            </ul>
          </section>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default Overview;
